import { useCallback, useEffect, useRef, useState } from 'react'
import type { SSEStatus, UseOneSSEOptions, UseOneSSEResult } from './types'

// ---------- 默认配置 ----------
const DEFAULTS = {
	autoConnect: true,
	reconnect: true,
	maxRetries: Infinity,
	baseRetryDelay: 1000,
	maxRetryDelay: 30_000,
	heartbeatTimeout: 45_000,
	connectTimeout: 10_000,
	withCredentials: false,
	pauseOnHidden: true,
	heartbeatEventName: 'heartbeat',
	debug: false
}

/**
 * OneSSE —— 项目内统一的 SSE（Server-Sent Events）订阅 hook。
 *
 * 与浏览器原生 EventSource 相比多做了：
 *  1. 自动重连 + 指数退避（1s → 2s → 4s → ... → 30s 封顶）
 *  2. 心跳检测：超过 heartbeatTimeout 没收到消息就视为断线，强制重连
 *  3. Page Visibility：标签页隐藏时暂停重连，回前台再继续（避免后台 tab 浪费连接）
 *  4. onMessage 默认 JSON.parse；解析失败原样返回
 *  5. 卸载 / url 变化时彻底清理 EventSource + 所有 timer
 *
 * 用法：
 *   const { status, data } = useOneSSE<{ count: number }>({
 *       url: buildSSEUrl('unreadCount'),  // '/sse/unreadCount/stream?satoken=xxx'
 *       eventName: 'unreadCount',
 *       onMessage: (msg) => setCount(msg.count)
 *   })
 *
 * 调试连通性：传 `debug: true` 会在事件触发时打印日志（connecting/open/message/error/retry/heartbeat timeout），
 * 无定时轮询，零开销。
 */
export function useOneSSE<T = unknown>(options: UseOneSSEOptions<T>): UseOneSSEResult<T> {
	const {
		url,
		eventName,
		onMessage,
		onOpen,
		onError,
		autoConnect = DEFAULTS.autoConnect,
		reconnect = DEFAULTS.reconnect,
		maxRetries = DEFAULTS.maxRetries,
		baseRetryDelay = DEFAULTS.baseRetryDelay,
		maxRetryDelay = DEFAULTS.maxRetryDelay,
		heartbeatTimeout = DEFAULTS.heartbeatTimeout,
		connectTimeout = DEFAULTS.connectTimeout,
		withCredentials = DEFAULTS.withCredentials,
		pauseOnHidden = DEFAULTS.pauseOnHidden,
		heartbeatEventName = DEFAULTS.heartbeatEventName,
		debug = DEFAULTS.debug,
		EventSourceImpl
	} = options

	// ---------- 状态 ----------
	const [status, setStatus] = useState<SSEStatus>('idle')
	const [data, setData] = useState<T | null>(null)
	const [error, setError] = useState<Event | null>(null)
	const [retryCount, setRetryCount] = useState(0)

	// ---------- 内部可变 ref（不触发渲染） ----------
	const esRef = useRef<EventSource | null>(null)
	const esIdRef = useRef(0) // 每个 EventSource 一个递增 ID，方便排查多个连接并发
	const retryTimerRef = useRef<number | null>(null)
	const heartbeatTimerRef = useRef<number | null>(null)
	const connectTimerRef = useRef<number | null>(null)
	const retryCountRef = useRef(0) // ⚠️ 必须用 ref：state 闭包陈旧会导致指数退避失效
	const isUnmountedRef = useRef(false)
	const isHiddenRef = useRef(typeof document !== 'undefined' && document.hidden)
	const isManuallyClosedRef = useRef(false) // 调过 disconnect() 后不再自动重连

	// 用 ref 持有"最新的 callback"，避免 url 不变但回调换了导致 EventSource 被反复重建
	const onMessageRef = useRef(onMessage)
	const onOpenRef = useRef(onOpen)
	const onErrorRef = useRef(onError)
	useEffect(() => {
		onMessageRef.current = onMessage
	}, [onMessage])
	useEffect(() => {
		onOpenRef.current = onOpen
	}, [onOpen])
	useEffect(() => {
		onErrorRef.current = onError
	}, [onError])

	// ---------- 工具：清 timer ----------
	const clearTimers = useCallback(() => {
		if (retryTimerRef.current != null) {
			window.clearTimeout(retryTimerRef.current)
			retryTimerRef.current = null
		}
		if (heartbeatTimerRef.current != null) {
			window.clearTimeout(heartbeatTimerRef.current)
			heartbeatTimerRef.current = null
		}
		if (connectTimerRef.current != null) {
			window.clearTimeout(connectTimerRef.current)
			connectTimerRef.current = null
		}
	}, [])

	// ---------- 工具：心跳重置（每次收到任意消息都调用） ----------
	const armHeartbeat = useCallback(() => {
		if (heartbeatTimeout <= 0) return
		if (heartbeatTimerRef.current != null) {
			window.clearTimeout(heartbeatTimerRef.current)
		}
		heartbeatTimerRef.current = window.setTimeout(() => {
			if (debug) {
				// eslint-disable-next-line no-console
				console.warn(`[OneSSE] heartbeat timeout (${heartbeatTimeout}ms), closing connection`)
			}
			// 超时：主动断开，触发 onerror + 重连逻辑
			if (esRef.current) {
				esRef.current.close()
			}
		}, heartbeatTimeout)
	}, [heartbeatTimeout, debug])

	// ---------- 关闭当前连接（不触发重连） ----------
	const closeCurrent = useCallback(() => {
		if (esRef.current) {
			esRef.current.close()
			esRef.current = null
		}
		clearTimers()
	}, [clearTimers])

	// ---------- 建立连接 ----------
	const connect = useCallback(() => {
		if (isUnmountedRef.current) return
		closeCurrent()
		isManuallyClosedRef.current = false

		// 标签页隐藏 + 允许暂停 → 不连
		if (pauseOnHidden && isHiddenRef.current) {
			setStatus('closed')
			return
		}

		setStatus('connecting')
		setError(null)

		if (debug) {
			// eslint-disable-next-line no-console
			console.log(`[OneSSE] connecting: ${url}`)
		}

		const ES = EventSourceImpl ?? (typeof window !== 'undefined' ? window.EventSource : undefined)
		if (!ES) {
			// 浏览器不支持 SSE（极旧环境），降级成 error
			const fakeEvt = new Event('error')
			setError(fakeEvt)
			setStatus('error')
			onErrorRef.current?.(fakeEvt)
			return
		}

		const es = new ES(url, { withCredentials })
		esRef.current = es
		esIdRef.current += 1
		const esId = esIdRef.current
		if (debug) {
			// eslint-disable-next-line no-console
			console.log(`[OneSSE] #${esId} new EventSource created at ${new Date().toLocaleTimeString()}`)
		}

		// 连接超时：超过 connectTimeout 毫秒 onopen 还没触发就主动断开，走重连逻辑
		if (connectTimeout > 0) {
			connectTimerRef.current = window.setTimeout(() => {
				if (isUnmountedRef.current) return
				if (esRef.current && esRef.current.readyState !== EventSource.OPEN) {
					if (debug) {
						// eslint-disable-next-line no-console
						console.warn(`[OneSSE] connect timeout (${connectTimeout}ms), closing connection`)
					}
					esRef.current.close()
				}
			}, connectTimeout)
		}

		const handler = (e: MessageEvent) => {
			if (isUnmountedRef.current) return
			armHeartbeat()
			let parsed: T
			try {
				parsed = JSON.parse(e.data) as T
			} catch {
				// 后端发的是纯文本时，原样透传
				parsed = e.data as unknown as T
			}
			if (debug) {
				// eslint-disable-next-line no-console
				console.log('[OneSSE] message:', parsed)
			}
			setData(parsed)
			onMessageRef.current?.(parsed, e)
		}

		// 心跳：独立 handler，只重置心跳定时器 + 打 debug 日志，**不**触发 onMessage
		// （业务侧不需要关心心跳事件，且心跳 payload 通常没有业务字段，混进 onMessage 会污染状态）
		const heartbeatHandler = () => {
			if (isUnmountedRef.current) return
			if (debug) {
				// eslint-disable-next-line no-console
				console.log(`[OneSSE] heartbeat at ${new Date().toLocaleTimeString()}`)
			}
			armHeartbeat()
		}

		// 同时监听默认通道（onmessage）和具名事件 —— 后端 send / sendEvent 不一致时也不会丢消息
		es.onmessage = handler
		if (eventName) {
			es.addEventListener(eventName, handler as EventListener)
		}
		if (heartbeatEventName) {
			es.addEventListener(heartbeatEventName, heartbeatHandler as EventListener)
		}

		es.onopen = () => {
			if (isUnmountedRef.current) return
			setStatus('open')
			retryCountRef.current = 0 // ⚠️ 必须同时重置 ref（state 重置是异步的）
			setRetryCount(0) // 成功就连上就清零
			if (connectTimerRef.current != null) {
				window.clearTimeout(connectTimerRef.current)
				connectTimerRef.current = null
			}
			armHeartbeat()
			if (debug) {
				// eslint-disable-next-line no-console
				console.log(`[OneSSE] #${esId} open at ${new Date().toLocaleTimeString()}: ${url}`)
			}
			onOpenRef.current?.()
		}

		es.onerror = (e) => {
			if (isUnmountedRef.current) return
			setError(e)
			setStatus('error')
			if (debug) {
				// eslint-disable-next-line no-console
				const readyStateMap: Record<number, string> = { 0: 'CONNECTING', 1: 'OPEN', 2: 'CLOSED' }
				const readyStateName = readyStateMap[es.readyState] ?? `UNKNOWN(${es.readyState})`
				// eslint-disable-next-line no-console
				console.warn(
					`[OneSSE] #${esId} error at ${new Date().toLocaleTimeString()}: readyState=${readyStateName} ` +
						`(常见原因：CORS 拒绝 / 401/403 / 后端关闭连接 / 网络中断)`
				)
			}
			onErrorRef.current?.(e)
			// ⚠️ 必须显式 close：浏览器对 EventSource 的 auto-close 不是立刻的，
			// 不显式关闭的话底层 TCP 连接会一直挂着，多次 retry 后 Network 标签会堆一堆 (pending)
			if (esRef.current) {
				try {
					esRef.current.close()
				} catch {
					/* ignore */
				}
			}
			esRef.current = null
			clearTimers()

			if (!reconnect || isManuallyClosedRef.current) {
				setStatus('closed')
				return
			}
			if (retryCountRef.current >= maxRetries) {
				setStatus('closed')
				return
			}
			if (pauseOnHidden && isHiddenRef.current) {
				// 标签页隐藏时先挂着，等 visible 再连
				setStatus('closed')
				return
			}
			// 退避策略：
			//   - 第一次 retry（retryCountRef.current === 0）立即触发，不等延迟
			//     避免堆积挂起请求，也让"快速可恢复"的场景（比如后端瞬时抖动）能立刻恢复
			//   - 第一次 retry 失败后，才开始指数退避：1s → 2s → 4s → ... → 30s
			// ⚠️ 用 retryCountRef.current，不要用 retryCount（state 在闭包里是旧值）
			const isFirstRetry = retryCountRef.current === 0
			const delay = isFirstRetry ? 0 : Math.min(baseRetryDelay * 2 ** (retryCountRef.current - 1), maxRetryDelay)
			if (debug) {
				// eslint-disable-next-line no-console
				console.log(`[OneSSE] retry #${retryCountRef.current + 1} in ${delay}ms at ${new Date().toLocaleTimeString()}`)
			}
			retryTimerRef.current = window.setTimeout(() => {
				if (isUnmountedRef.current) return
				retryCountRef.current += 1
				setRetryCount(retryCountRef.current)
				connect()
			}, delay)
		}
	}, [
		url,
		eventName,
		withCredentials,
		reconnect,
		maxRetries,
		baseRetryDelay,
		maxRetryDelay,
		pauseOnHidden,
		connectTimeout,
		heartbeatEventName,
		debug,
		armHeartbeat,
		clearTimers,
		closeCurrent,
		EventSourceImpl
	])

	// ---------- 主动断开 ----------
	const disconnect = useCallback(() => {
		isManuallyClosedRef.current = true
		closeCurrent()
		setStatus('closed')
	}, [closeCurrent])

	// ---------- 生命周期：url/eventName 变化时重连 ----------
	useEffect(() => {
		isUnmountedRef.current = false
		if (autoConnect) {
			connect()
		}
		return () => {
			isUnmountedRef.current = true
			closeCurrent()
		}
	}, [url, eventName, autoConnect])

	// ---------- Page Visibility：前台再续上 ----------
	useEffect(() => {
		if (!pauseOnHidden || typeof document === 'undefined') return
		const onVis = () => {
			const hidden = document.hidden
			isHiddenRef.current = hidden
			if (!hidden && status === 'closed' && !isManuallyClosedRef.current && autoConnect) {
				// 切回前台时尝试重连
				connect()
			}
		}
		document.addEventListener('visibilitychange', onVis)
		return () => document.removeEventListener('visibilitychange', onVis)
	}, [pauseOnHidden, status, autoConnect, connect])

	return { status, data, error, retryCount, connect, disconnect }
}
